// DESKTOP-ONLY (runs before Vue boots)
import { useUserback } from '@/composables';

const isMobile = () => {
  if (navigator.userAgentData) {
    return navigator.userAgentData.mobile;
  }

  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return mobileRegex.test(navigator.userAgent);
};

let blocked = false;

function blockApp() {
  if (blocked) return;
  blocked = true;

  document.open();
  document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Desktop Required</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
      </head>
      <body style="
        margin:0;
        height:100vh;
        display:flex;
        align-items:center;
        justify-content:center;
        background:#020617;
        color:#ffffff;
        font-family:system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
      ">
        <div style="text-align:center; max-width:420px; padding:32px;">
          <h1 style="font-size:28px; margin-bottom:12px;">
            Desktop Required
          </h1>
          <p style="opacity:0.85;">
            You must be on a computer to view this website.
          </p>
        </div>
      </body>
    </html>
  `);
  document.close();

  throw new Error('Blocked: viewport below 1024px');
}

if (isMobile()) {
  blockApp();
}

import { createPinia } from 'pinia';
import { createApp } from 'vue';

import App from './App';

import '@/assets/styles/main.scss';
import '@/fontAwesome';

import { router } from '@/router/router';

import * as Sentry from '@sentry/vue';
import config from '@/config';
const { initUserback } = useUserback();

const app = createApp(App);

if (config.userbackToken) {
  initUserback(config.userbackToken, {
    autohide: false,
  });
}

// Add Sentry integration for Vue Router after it's initialized
if (config.sentryDsn) {
  // Only initialise Sentry if the DSN has been set, which is generally only in Staging or Production
  Sentry.init({
    app,
    dsn: config.sentryDsn,
    sendDefaultPii: true,
    environment: config.env,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserTracingIntegration({ router }),
    ],
    replaysSessionSampleRate: config.env === 'production' ? 0.1 : 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  });
  Sentry.attachErrorHandler(app, {
    logErrors: config.env === 'development', // Log errors in dev
  });
}

// Conditionally import and initialize Clarity
if (typeof window !== 'undefined' && config.clarityProjectId) {
  import('@microsoft/clarity')
    .then((module) => {
      const Clarity = module.default || module;
      if (typeof Clarity.init === 'function') {
        Clarity.init(config.clarityProjectId);
        console.log(`[Clarity] Initialized for ${config.env}.`);
      } else {
        console.warn('[Clarity] init function not found on module.');
      }
    })
    .catch((error) => {
      console.error('Failed to load Clarity:', error);
    });
}

const pinia = createPinia();

const vFocus = {
  mounted: (el) => {
    // If the element itself is an input, focus it
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      el.focus();
    }
    // Otherwise, find the input inside the component wrapper
    else {
      const input = el.querySelector('input, textarea');
      if (input) {
        input.focus();
      }
    }
  },
};

const vClickOutside = {
  mounted: (el, binding) => {
    el.clickOutsideEvent = function(event) {
      // check if the click was outside the el and his childrens
      if (el !== event.target && !el.contains(event.target)) {
        // check if the provided expression was function

        // if (typeof vnode.context[binding.value] !== 'function') {
        //   return;
        // }
        // // and if it was a function - call it
        if (binding.value && typeof binding.value === 'function') {
          binding.value(event);
        }
      }
    };

    document.body.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted: el => {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  },
};

app
  .use(router)
  .use(pinia);

app.directive('focus', vFocus);
app.directive('click-outside', vClickOutside);

app.mount('#app');
